﻿import React, { useState, useEffect } from 'react';
import { Document } from '../models/document';
import { DocumentType, KarlFischerType } from '../models/enums';
import { KarlFischerSample } from '../models/karl-fischer-sample';
import { KarlFischerData } from '../models/karl-fischer-data';
import { KarlFischerQcCheck } from '../models/karl-fischer-qc-check';
import { LookupItem } from '../models/lookup-item';
import { FieldUpdate } from '../models/field-update';
import { CrudFunctions } from '../utilities/crud-functions';
import SingleDocumentLoader from './SingleDocumentLoader';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { QcCheck } from '../models/qc-check';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    Paging,
    Editing,
    LoadPanel,
    Scrolling,
    Selection,
    Toolbar,
    Button as ColumnButton,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import Toast from 'devextreme-react/toast';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export interface Props {
    benchSheetId: string;
    karlFischerType: KarlFischerType;
    readOnly?: boolean;
}

export default function KarlFischerSamples(props: Props) {
    const [sampleData, setSampleData] =
        useState<KarlFischerData>();

    const [samplesSpreadsheetName, setSamplesSpreadsheetName] =
        useState<string>();

    const [uoms, setUoms] =
        useState<LookupItem[]>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div className="pw-comp-row">
                <SingleDocumentLoader label="Samples Spreadsheet" readOnly={props.readOnly}
                    documentName={samplesSpreadsheetName}
                    onDocumentLoaded={onKarlFischerSamplesSpreadsheetLoaded} />
            </div>

            {props.karlFischerType === KarlFischerType.Titration &&
                <div className="pw-comp-row">
                    <DataGrid
                        height="16rem"
                        width="100%"
                        keyExpr="karlFischerSampleId"
                        dataSource={sampleData && sampleData.titer ?
                            sampleData.titer : []}
                        onRowRemoving={onSampleRemoving}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        rowAlternationEnabled={true}
                        showBorders={true} >

                        <Paging enabled={false} />

                        <Column type="buttons" width="6rem">
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={onQcChecksAuditButtonClicked} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onQcChecksInformationButtonClicked} />
                            <ColumnButton name="delete" hint="delete row" />
                        </Column>

                        <Column dataField='partNumber' caption="Catalog #" width='8rem' allowEditing={false} />
                        <Column dataField='lotNumber' width='8rem' allowEditing={false} />
                        <Column dataField='sampleTypeString' caption="Sample Type" width='7rem' allowEditing={false} />
                        <Column dataField='sampleSize' caption="Size" dataType="number" width='5rem' allowEditing={false} />
                        <Column dataField='sampleSizeUomString' caption="UOM" width='4rem' allowEditing={false} />
                        <Column dataField='waterContentStandardPercent' caption="Standard Water Content" dataType="number" width='8rem' allowEditing={false} />
                        <Column dataField='waterContentStandardPercentUomString' caption="UOM" dataType="string" width='5rem' allowEditing={false} />
                        <Column dataField='titer' dataType="number" width='8rem' allowEditing={false} />
                        <Column dataField='titerUomString' caption="UOM" dataType="string" width='5rem' allowEditing={false} />
                        
                        <Column />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Titer</div>
                                </div>
                            </ToolbarItem>
                        </Toolbar>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={false}
                            allowUpdating={false}
                            allowDeleting={true} />
                    </DataGrid>
                </div>
            }

            <div className="pw-comp-row pw-margin-top-1">
                <DataGrid
                    height="16rem"
                    width="100%"
                    keyExpr="karlFischerSampleId"
                    dataSource={sampleData && sampleData.qcChecks ?
                        sampleData.qcChecks : []}
                    onRowValidating={onQcChecksRowValidating}
                    onRowRemoving={onSampleRemoving}
                    wordWrapEnabled={true}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    showBorders={true} >

                    <Paging enabled={false} />

                    <Column type="buttons" width="7rem">
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onQcChecksAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onQcChecksInformationButtonClicked} />
                        <ColumnButton name="edit" hint="edit row" />
                        <ColumnButton name="delete" hint="delete row" />
                    </Column>

                    <Column dataField='partNumber' caption="Catalog #" width='8rem' allowEditing={false} />
                    <Column dataField='lotNumber' width='8rem' allowEditing={false} />
                    <Column dataField='sampleTypeString' caption="Sample Type" width='6rem' allowEditing={false} />
                    <Column dataField='sampleSize' width='5rem' allowEditing={false} />
                    <Column dataField='sampleSizeUomString' caption="UOM" width='4rem' allowEditing={false} />

                    {/*
                        {props.karlFischerType === KarlFischerType.Titration &&
                            <Column dataField='waterContentStandardPercent' caption="Standard Water Content" dataType="number" width='8rem' allowEditing={false} />
                        }
                        {props.karlFischerType === KarlFischerType.Titration &&
                            <Column dataField='waterContentStandardPercentUomString' caption="UOM" dataType="string" width='4rem' allowEditing={false} />
                        }
                        */}

                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='dilutionFactor' dataType="number" width='6rem' allowEditing={false} />
                    }

                    <Column dataField='waterContentPercent' caption="Water Content (%)" dataType="number" width='8rem' allowEditing={false} />

                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='titer' dataType="number" width='6rem' allowEditing={false} />
                    }
                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='titerUomString' caption="UOM" dataType="string" width='4rem' allowEditing={false} />
                    }

                    {props.karlFischerType === KarlFischerType.Coulometric &&
                        <Column dataField='waterContentPpm' caption="Water Content (PPM)" dataType="number" width='8rem' allowEditing={false} />
                    }

                    <Column dataField='assignedValue' width='6rem' dataType="number" />

                    {/*
                        <Column dataField='assignedValueUomString' caption="UOM" width='4rem' allowEditing={false} />
                        */}
                    <Column dataField='pass' width='6rem' allowEditing={false}
                        cellRender={qcCheckPassCellRender} />

                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">QC Checks</div>
                            </div>
                        </ToolbarItem>
                    </Toolbar>

                    <Editing mode="row"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={!props.readOnly}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            <div className="pw-comp-row pw-margin-top-1">
                <DataGrid
                    height="16rem"
                    width="100%"
                    wordWrapEnabled={true}
                    keyExpr="karlFischerSampleId"
                    onRowRemoving={onSampleRemoving}
                    dataSource={sampleData && sampleData.samples ?
                        sampleData.samples : []}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    showBorders={true} >

                    <Paging enabled={false} />

                    <LoadPanel enabled={true} />
                    <Scrolling columnRenderingMode="virtual" />
                    <Selection mode='single' />

                    <Column type="buttons" width="6rem">
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onSamplesAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onSamplesInformationButtonClicked} />
                        <ColumnButton name="delete" hint="delete row" />
                    </Column>

                    <Column dataField='partNumber' caption="Catalog #" width='8rem' allowEditing={false} />
                    <Column dataField='lotNumber' width='8rem' allowEditing={false} />
                    <Column dataField='sampleTypeString' caption="Sample Type" width='7rem' allowEditing={false} />
                    <Column dataField='sampleSize' caption="Size" dataType="number" width='5rem' allowEditing={false} />
                    <Column dataField='sampleSizeUomString' caption="UOM" width='4rem' allowEditing={false} />

                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='dilutionFactor' dataType="number" width='6rem' allowEditing={false} />
                    }

                    <Column dataField='waterContentPercent' caption="Water Content (%)" dataType="number" width='8rem' allowEditing={false} />

                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='titer' dataType="number" width='8rem' allowEditing={false} />
                    }
                    {props.karlFischerType === KarlFischerType.Titration &&
                        <Column dataField='titerUomString' caption="UOM" dataType="string" width='5rem' allowEditing={false} />
                    }

                    {props.karlFischerType === KarlFischerType.Coulometric &&
                        <Column dataField='waterContentPpm' caption="Water Content (PPM)" dataType="number" width='8rem' allowEditing={false} />
                    }
                    {props.karlFischerType === KarlFischerType.Coulometric &&
                        <Column dataField='ovenTemperature' caption="Oven Temp (°C)" width='7rem' allowEditing={false} />
                    }

                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Samples</div>
                            </div>
                        </ToolbarItem>
                    </Toolbar>

                    <Editing mode="cell"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={false}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            <div className="pw-margin-top-1">
                <div className="lq-sub-title pw-blue">Summary Data</div>

                <div className="pw-comp-row">
                    <DataGrid
                        height="12rem"
                        width="100%"
                        dataSource={sampleData && sampleData.summaries ? sampleData.summaries : []}
                        wordWrapEnabled={true}
                        showBorders={true} >
                        <Paging enabled={false} />

                        <Column dataField='partNumber' dataType="string" caption="Catalog #" width='12rem' />
                        <Column dataField='lotNumber' dataType="string" width='12rem' />
                        <Column dataField='waterContentPercentAverage' dataType="number" caption="Water Content % Avg" width='12rem' />
                        <Column dataField='waterContentRsd' caption='Water Content% RSD' dataType="number" width='12rem' />
                        <Column />

                    </DataGrid>
                </div>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    /*
    async function onSampleChanged(fieldUpdate: FieldUpdate) {
        updateKarlFischerSampleField(fieldUpdate)
            .then(() => {
                getSamples();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Samples");
            });
    }
    */

    async function onKarlFischerSamplesSpreadsheetLoaded(samplesSpreadsheet: Document) {
        samplesSpreadsheet.documentType = DocumentType.CSV;

        await uploadSamplesSpreadsheet(samplesSpreadsheet)
            .then(() => {
                getSamples();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Uploading Samples Data");
            });
    }

    function onQcChecksRowValidating(e: any) {
        if (e.newData && Object.keys(e.newData).length > 0) {
            let qcCheck = e.oldData as KarlFischerQcCheck;

            if (qcCheck) {
                let updatedQcCheck: KarlFischerQcCheck = { ...qcCheck, ...e.newData };

                onQcChecksChanged(updatedQcCheck);
            }
        }
    }

    /*
    function onSamplesRowValidating(e: any) {
        //FIX THIS - different object now

        //update
        if (e.oldData && Object.keys(e.oldData).length > 0) {

            let sample = e.oldData as KarlFischerSample;

            if (sample) {
                let updatedSample = { ...sample, ...e.newData };

                //if (props.onSampleChanged)
                onSampleChanged(updatedSample);
            }
        }
    }
    */

    async function onQcChecksChanged(e: any) {
        if (e && e.karlFischerSampleId && sampleData && sampleData.qcChecks) {
            const qcChecks = [...sampleData.qcChecks];

            if (qcChecks) {
                let matchingQcCheck = qcChecks.find(q => q.karlFischerSampleId === e.karlFischerSampleId);

                if (matchingQcCheck) {
                    matchingQcCheck.assignedValue = e.assignedValue;

                    const fieldUpdate: FieldUpdate = {
                        entity: 'KarlFischerData',
                        field: 'qcAssignedValue',
                        rowId: e.karlFischerSampleId,
                        value: e.assignedValue
                    }

                    setSampleData({ ...sampleData, qcChecks: qcChecks });

                    updateKarlFischerQcCheckAssignedValue(fieldUpdate)
                        .then(() => {
                            getSamples();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Updating QC Checks");
                        });
                }
            }
        }
    }

    function onSampleRemoving(e: any) {
        if (e.data && e.data.karlFischerSampleId)
            deleteKarlFischerSample(e.data.karlFischerSampleId)
                .then(() => {
                    showToast("Sample row deleted", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Glassware Item");
                });
    }

    async function uploadSamplesSpreadsheet(samplesSpreadSheet: Document) {
        if (props.benchSheetId) {
            if (props.karlFischerType === KarlFischerType.Titration) {
                return CrudFunctions.postData(`benchsheets/karlfischertitrationdata/${props.benchSheetId}`, samplesSpreadSheet);
            }
            else {
                return CrudFunctions.postData(`benchsheets/karlfischercoulometricdata/${props.benchSheetId}`, samplesSpreadSheet);
            }
        }
    }

    async function getSamples() {
        await CrudFunctions.getData(`benchsheets/karlfischersamples/${props.benchSheetId}`)
            .then(data => {
                setSampleData(data)
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Samples");
            });
    }

    async function getUoms() {
        await CrudFunctions.getData("common/uoms")
            .then(data => {
                setUoms(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving UOMs");
            });
    }

    // PUT functions

    /*
    async function updateKarlFischerSampleField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/karlfischersamplefield", fieldUpdate);
    }
    */

    async function updateKarlFischerQcCheckAssignedValue(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/karlfischerqccheckassignedvalue", fieldUpdate);
    }

    async function deleteKarlFischerSample(karlFischerSampleId: string) {
        return CrudFunctions.deleteData(`benchsheets/karlfischerdatum/${karlFischerSampleId}`);
    }

    async function getAllData() {
        await Promise.all([
            getUoms(),
            getSamples()
        ]);
    }

    function onQcChecksAuditButtonClicked(e: any) {

    }

    function onQcChecksInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const qcCheck = e.row.data as QcCheck;

            if (qcCheck) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: qcCheck.qcCheckId,
                    createTimestamp: qcCheck.createTimestamp,
                    lastModifiedTimestamp: qcCheck.modifyTimestamp,
                    createUser: qcCheck.createUser,
                    modifyUser: qcCheck.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function onSamplesAuditButtonClicked(e: any) {

    }

    function onSamplesInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const sample = e.row.data as KarlFischerSample;

            if (sample) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: sample.karlFischerSampleId,
                    createTimestamp: sample.createTimestamp,
                    lastModifiedTimestamp: sample.modifyTimestamp,
                    createUser: sample.createUser,
                    modifyUser: sample.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //render

    function sampleSizeUomCellRender(e: any) {
        if (e.data && e.data.sampleSizeUom) {
            let data = e.data.sampleSizeUom;

            return (
                <div>
                    <div className="item-main">{data.name}</div>
                    <div className="item-sub">{data.description}</div>
                </div>
            );
        }
        else
            return;
    }

    function qcCheckPassCellRender(e: any) {
        if (e.data && e.data.pass) {
            return (
                <div>
                    <div className="valid-data">PASS</div>
                </div>
            );
        }
        else
            return (
                <div>
                    <div className="invalid-data">FAIL</div>
                </div>
            );
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}
